import React, { useState, useEffect, useRef } from "react";
import { Flex, Typography, notification } from "antd";
import logoImg from "../../assets/logo/dealVoice.png";
import { getStagnantDeals } from "../../../services/pipelineService";
import { getTodayActivities } from "../../../services/activityService";
import "./splash.css";

const { Text } = Typography;

const STAGES = [
  { percent: 20, text: "Signing you in..." },
  { percent: 45, text: "Checking today's tasks & deal notifications..." },
  { percent: 80, text: "Setting up your workspace..." },
  { percent: 100, text: "Ready!" },
];

function SplashScreen({ user, companyName, isReady = false, onFinish, minDurationMs = 1200, maxTimeoutMs = 15000 }) {
  const [progress, setProgress] = useState(15);
  const [stageText, setStageText] = useState(STAGES[0].text);
  const [isFading, setIsFading] = useState(false);
  const [stagnantDeals, setStagnantDeals] = useState([]);
  const [todayActivities, setTodayActivities] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);

  const startTimeRef = useRef(Date.now());
  const completedRef = useRef(false);
  const dataLoadedRef = useRef(false);
  // Track alert data so post-splash notification can still fire
  const stagnantDealsRef = useRef([]);
  const todayActivitiesRef = useRef([]);
  const notificationFiredRef = useRef(false);

  useEffect(() => {
    dataLoadedRef.current = dataLoaded;
  }, [dataLoaded]);

  const splashFetchDoneRef = useRef(false);

  const fireAlertNotification = (deals, activities) => {
    if (notificationFiredRef.current) return;
    if (deals.length === 0 && activities.length === 0) return;
    notificationFiredRef.current = true;

    const parts = [];
    if (activities.length > 0) {
      parts.push(`${activities.length} scheduled ${activities.length === 1 ? "activity" : "activities"} today`);
    }
    if (deals.length > 0) {
      parts.push(`${deals.length} ${deals.length === 1 ? "deal stuck" : "deals stuck"} for more than 7 days in stage`);
    }

    notification.info({
      message: "Today's Overview & Alerts",
      description: `You have ${parts.join(" and ")}. Check the notification bell in the top navbar.`,
      placement: "topRight",
      duration: 6,
    });
  };

  // Fetch deal notifications and today activities during splashscreen
  useEffect(() => {
    if (splashFetchDoneRef.current) return;
    const effectiveCompanyCode = companyName || user?.companyCode;
    const isEmployee = user?.role === "employee" || user?.role === "sales_rep";
    const employeeId = isEmployee ? user?.id || user?._id || user?.employeeId : null;

    if (effectiveCompanyCode) {
      splashFetchDoneRef.current = true;
      const dealOptions = { days: 7 };
      const actOptions = {};
      if (isEmployee && employeeId) {
        dealOptions.employeeId = employeeId;
        actOptions.employeeId = employeeId;
      }

      Promise.all([getStagnantDeals(effectiveCompanyCode, dealOptions), getTodayActivities(effectiveCompanyCode, actOptions)])
        .then(([dealsRes, actRes]) => {
          const deals = dealsRes?.success && Array.isArray(dealsRes.deals) ? dealsRes.deals : [];
          const activities = actRes?.success && Array.isArray(actRes.data) ? actRes.data : [];

          stagnantDealsRef.current = deals;
          todayActivitiesRef.current = activities;

          setStagnantDeals(deals);
          setTodayActivities(activities);

          // If splash already completed before data arrived, fire notification now
          if (completedRef.current) {
            fireAlertNotification(deals, activities);
          }
        })
        .catch(() => {})
        .finally(() => {
          setDataLoaded(true);
        });
    } else {
      setDataLoaded(true);
    }
  }, [companyName, user]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (completedRef.current) return;

      const elapsed = Date.now() - startTimeRef.current;
      const minDurationMet = elapsed >= minDurationMs;
      const maxTimeoutHit = elapsed >= maxTimeoutMs;
      const isDataReady = dataLoadedRef.current || maxTimeoutHit;
      const shouldComplete = (isReady && minDurationMet && isDataReady) || maxTimeoutHit;

      if (!isDataReady) {
        // Phase 1: Smoothly progress up to 50% while loading today's activities & stuck deals
        setProgress((prev) => {
          if (prev < 50) {
            const step = Math.max(1, Math.round((50 - prev) * 0.1));
            return Math.min(prev + step, 50);
          }
          return 50;
        });
      } else if (!shouldComplete) {
        // Phase 2: Data loaded! Continue setting up workspace up to 88%
        setProgress((prev) => {
          if (prev < 88) {
            const step = Math.max(1, Math.round((88 - prev) * 0.08));
            return Math.min(prev + step, 88);
          }
          return 88;
        });
      } else {
        // Phase 3: Dashboard ready, smoothly accelerate to 100%
        setProgress((prev) => {
          if (prev < 100) {
            const step = Math.max(5, Math.round((100 - prev) * 0.45));
            return Math.min(prev + step, 100);
          }
          return 100;
        });
      }
    }, 40);

    return () => clearInterval(timer);
  }, [isReady, minDurationMs, maxTimeoutMs]);

  useEffect(() => {
    if (progress < 25) {
      setStageText(STAGES[0].text);
    } else if (progress < 50) {
      setStageText(STAGES[1].text);
    } else if (progress < 90) {
      setStageText(STAGES[2].text);
    } else {
      setStageText(STAGES[3].text);
    }

    if (progress >= 100 && !completedRef.current) {
      completedRef.current = true;
      setIsFading(true);

      // Fire notification using ref data (state may not be up-to-date if data arrived late)
      fireAlertNotification(stagnantDealsRef.current, todayActivitiesRef.current);

      const finishTimer = setTimeout(() => {
        if (onFinish) onFinish();
      }, 350);
      return () => clearTimeout(finishTimer);
    }
  }, [progress, onFinish]);

  const hasAlerts = stagnantDeals.length > 0 || todayActivities.length > 0;

  return (
    <div className={`dv-splash-minimal-backdrop ${isFading ? "dv-splash-fadeout" : ""}`}>
      <Flex vertical align="center" justify="center" className="dv-splash-minimal-content">
        {/* 1. Logo */}
        <img src={logoImg} alt="DealVoice" className="dv-splash-minimal-logo" />

        {/* 2. Sleek Line Progress Track & Shimmer Bar */}
        <div className="dv-splash-progress-track">
          <div className="dv-splash-progress-fill" style={{ width: `${progress}%` }} />
        </div>

        {/* 3. Percentage */}
        <Text className="dv-splash-percentage">{progress}%</Text>

        {/* 4. Stage Subtitle */}
        <Text className="dv-splash-stage-text">{stageText}</Text>

        {/* 5. Live Today Activities & Stagnant Deal Alert Slot */}
        <div className="dv-splash-alert-slot">
          {hasAlerts && (
            <div className="dv-splash-alert-info">
              {todayActivities.length > 0 && (
                <span className="dv-splash-stat-item">
                  <span className="dv-splash-stat-val">{todayActivities.length}</span> activities today
                </span>
              )}
              {todayActivities.length > 0 && stagnantDeals.length > 0 && <span className="dv-splash-stat-sep">|</span>}
              {stagnantDeals.length > 0 && (
                <span className="dv-splash-stat-item">
                  <span className="dv-splash-stat-val">{stagnantDeals.length}</span> deals stuck in pipeline
                </span>
              )}
            </div>
          )}
        </div>
      </Flex>
    </div>
  );
}

export default SplashScreen;
